import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react"
import { useReplayPreflight } from "@/hooks/useReplayPreflight"
import { useReplayTransport } from "@/hooks/useReplayTransport"
import { ReplayDateSeekSheet } from "@/components/lytick/ReplayDateSeekSheet"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useReplayStore, type ReplaySpeed } from "@/stores/replayStore"

const SPEEDS: ReplaySpeed[] = [0.5, 1, 2, 5, 10, 20, 50]

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] as const

function formatScrubberDate(epochMs: number | null): string {
  if (!epochMs) return "--"
  const d = new Date(epochMs)
  const h = d.getHours()
  const m = String(d.getMinutes()).padStart(2, "0")
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${h}:${m}`
}

function formatCandleDate(epochMs: number | null): string {
  if (!epochMs) return "--"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(epochMs)
}

export function ReplayTransportBar() {
  const {
    sessionState,
    isActive,
    currentCandle,
    startDate,
    speed,
    errorMessage,
    exit,
    togglePlayPause,
    stepBack,
    stepForward,
    setSpeed,
    position,
    anchorIndex,
    totalCandles,
  } = useReplayTransport()

  const { restartPreflight } = useReplayPreflight()
  const seekToTimestamp = useReplayStore((s) => s.seekToTimestamp)
  const setPosition = useReplayStore((s) => s.setPosition)

  // ── Scrubber ───────────────────────────────────────────────────────────────
  const progressBarRef = useRef<HTMLDivElement>(null)
  const dragTooltipRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [tooltipLeftPx, setTooltipLeftPx] = useState<number | null>(null)

  const anchorRelativeTotal = Math.max(1, totalCandles - 1 - anchorIndex)
  const anchorRelativePosition = Math.max(0, position - anchorIndex)
  const progressPercent =
    totalCandles > 1 ? (anchorRelativePosition / anchorRelativeTotal) * 100 : 0
  const clampedDotLeft = Math.max(0, Math.min(progressPercent, 100))

  useLayoutEffect(() => {
    if (!isDragging) {
      setTooltipLeftPx(null)
      return
    }
    const track = progressBarRef.current
    const tooltip = dragTooltipRef.current
    if (!track || !tooltip) return
    const trackWidth = track.offsetWidth
    const tooltipWidth = tooltip.offsetWidth
    const thumbCenter = (clampedDotLeft / 100) * trackWidth
    const half = tooltipWidth / 2
    setTooltipLeftPx(Math.max(half, Math.min(thumbCenter, trackWidth - half)))
  }, [isDragging, clampedDotLeft])

  const handleScrub = useCallback(
    (clientX: number) => {
      const bar = progressBarRef.current
      if (!bar || totalCandles <= 1) return
      const rect = bar.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      setPosition(anchorIndex + Math.round(ratio * anchorRelativeTotal))
    },
    [anchorIndex, anchorRelativeTotal, setPosition, totalCandles],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      setIsDragging(true)
      handleScrub(event.clientX)
      const onMove = (e: MouseEvent) => handleScrub(e.clientX)
      const onUp = () => {
        setIsDragging(false)
        window.removeEventListener("mousemove", onMove)
        window.removeEventListener("mouseup", onUp)
      }
      window.addEventListener("mousemove", onMove)
      window.addEventListener("mouseup", onUp)
    },
    [handleScrub],
  )

  const handleScrubberKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const step5 = Math.max(1, Math.round(anchorRelativeTotal * 0.05))
      const step1 = Math.max(1, Math.round(anchorRelativeTotal * 0.01))
      let next: number | null = null
      if (event.key === "ArrowRight") next = position + (event.shiftKey ? step1 : step5)
      else if (event.key === "ArrowLeft") next = position - (event.shiftKey ? step1 : step5)
      else if (event.key === "Home") next = anchorIndex
      else if (event.key === "End") next = anchorIndex + anchorRelativeTotal
      else return
      event.preventDefault()
      setPosition(Math.max(anchorIndex, Math.min(anchorIndex + anchorRelativeTotal, next)))
    },
    [anchorIndex, anchorRelativeTotal, position, setPosition],
  )

  const tooltipTimestamp = currentCandle?.timestamp ?? null

  if (sessionState === "live" || sessionState === "loading") return null

  return (
    <div
      className="animate-in fade-in-0 slide-in-from-bottom-2 pointer-events-auto absolute flex items-center gap-3 rounded-[var(--radius-lg)] border px-4 shadow-[var(--shadow-lg)]"
      style={{
        bottom: "var(--space-3)",
        left: "50%",
        transform: "translateX(-50%)",
        height: "var(--height-replay-bar)",
        backgroundColor: "var(--lyt-bg-elevated)",
        borderColor: "var(--lyt-border-default)",
        zIndex: "var(--z-nav)",
        animationDuration: "var(--duration-snap)",
      }}
    >
      {/* Error state */}
      {sessionState === "error" && (
        <>
          <span className="text-xs font-medium text-loss">{errorMessage ?? "Replay failed."}</span>
          <button
            type="button"
            onClick={() => void restartPreflight()}
            className="flex items-center gap-1.5 rounded-md bg-loss/10 px-3 py-1 text-xs font-semibold text-loss hover:bg-loss/20 transition-colors"
          >
            <RotateCcw className="size-3.5" /> Retry
          </button>
          <button
            type="button"
            onClick={exit}
            className="ml-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Exit
          </button>
        </>
      )}

      {/* Active transport */}
      {isActive && (
        <>
          {/* Exit */}
          <button
            type="button"
            onClick={exit}
            className="shrink-0 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Exit
          </button>

          {/* Divider */}
          <div className="h-5 w-px shrink-0 bg-border" />

          {/* Step back */}
          <button
            type="button"
            onClick={stepBack}
            aria-label="Step back"
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
          >
            <ChevronLeft className="size-4" />
          </button>

          {/* Play / Pause / Restart */}
          {sessionState === "ended" ? (
            <button
              type="button"
              onClick={() => void restartPreflight()}
              aria-label="Restart replay"
              className="flex size-8 items-center justify-center rounded-full border border-golden/30 text-golden/70 hover:bg-golden/10 hover:text-golden transition-colors"
            >
              <RotateCcw className="size-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={togglePlayPause}
              aria-label={sessionState === "playing" ? "Pause" : "Play"}
              title="Play / Pause (Space)"
              className="flex size-8 items-center justify-center rounded-full bg-golden/20 text-golden hover:bg-golden hover:text-background transition-colors"
            >
              {sessionState === "playing" ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
                  <rect x="1" y="1" width="4" height="10" rx="1" />
                  <rect x="7" y="1" width="4" height="10" rx="1" />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
                  <path d="M2.5 1.5l9 4.5-9 4.5z" />
                </svg>
              )}
            </button>
          )}

          {/* Step forward */}
          <button
            type="button"
            onClick={stepForward}
            aria-label="Step forward"
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
          >
            <ChevronRight className="size-4" />
          </button>

          {/* Divider */}
          <div className="h-5 w-px shrink-0 bg-border" />

          {/* Speed */}
          {sessionState !== "ended" && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title="Playback speed"
                  className="flex h-7 min-w-[40px] items-center justify-center gap-0.5 rounded bg-surface-hover px-1.5 text-xxs font-mono-tabular font-bold text-muted-foreground hover:bg-golden/20 hover:text-golden transition-colors"
                >
                  {speed}×
                  <ChevronDown className="size-3 opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="center"
                side="top"
                sideOffset={8}
                className="w-[168px] border p-2"
                style={{ backgroundColor: "var(--bg-elevated)", borderColor: "var(--border-default)" }}
              >
                <div className="grid grid-cols-4 gap-1">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSpeed(s)}
                      className={cn(
                        "rounded-lg px-2 py-1.5 text-xs font-mono-tabular transition-colors",
                        speed === s
                          ? "bg-golden font-bold text-background"
                          : "text-muted-foreground hover:bg-surface-hover",
                      )}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* FF badge */}
          {speed >= 16 && sessionState === "playing" && (
            <span className="select-none rounded bg-golden px-1 py-0.5 text-xxs font-bold text-background">
              FF
            </span>
          )}

          {/* Divider */}
          <div className="h-5 w-px shrink-0 bg-border" />

          {/* ── Timeline scrubber ── */}
          {sessionState !== "ended" && (
            <div className="relative mx-1 flex w-36 shrink-0 items-center">
              <div
                ref={progressBarRef}
                className="relative h-2 w-full rounded-full bg-muted scrub-cursor group ring-1 ring-inset ring-white/[0.08]"
                onMouseDown={handleMouseDown}
                onMouseLeave={() => setIsDragging(false)}
                onKeyDown={handleScrubberKeyDown}
                role="slider"
                aria-label="Replay position"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progressPercent)}
                tabIndex={0}
              >
                <div
                  className="h-full rounded-full transition-all duration-100 ease-linear"
                  style={{ width: `${progressPercent}%`, background: "var(--gradient-replay)" }}
                />
                <div
                  className="absolute top-1/2 size-3 -translate-y-1/2 rounded-full bg-golden shadow-[0_0_8px_var(--color-golden)] transition-all duration-100 ease-linear hover:scale-150"
                  style={{ left: `calc(${clampedDotLeft}% - 6px)` }}
                />

                {isDragging && (
                  <div
                    ref={dragTooltipRef}
                    className="absolute bottom-[calc(100%+10px)] pointer-events-none whitespace-nowrap z-50"
                    style={{
                      left: tooltipLeftPx != null ? `${tooltipLeftPx}px` : `${clampedDotLeft}%`,
                      transform: "translateX(-50%)",
                    }}
                  >
                    <div
                      className="rounded-lg px-3 py-1.5 text-white text-xs leading-tight font-medium"
                      style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
                    >
                      {formatScrubberDate(tooltipTimestamp)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Date readout — click to seek */}
          <ReplayDateSeekSheet
            onConfirm={seekToTimestamp}
            initialDate={currentCandle?.timestamp ?? startDate ?? null}
          >
            <button
              type="button"
              title="Seek to date"
              className="flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-xs font-mono-tabular text-foreground hover:bg-surface-hover transition-colors"
            >
              <CalendarDays className="size-3.5 shrink-0 text-muted-foreground" />
              {formatCandleDate(currentCandle?.timestamp ?? startDate)}
            </button>
          </ReplayDateSeekSheet>
        </>
      )}
    </div>
  )
}
